/**
 * ROISimulator — investor-facing ROI calculator (recovered 2028-08-26).
 *
 * Retirada del tab "Performance" en la limpieza UX previa, la calculadora
 * en sí se conserva porque sigue siendo útil como escenario "what-if".
 * Ahora vive dentro de "Mi proyecto" como bloque expandible, no como tab
 * independiente. `Bookings` sigue siendo la fuente del rendimiento real.
 *
 * Este módulo es puro cliente: no llama a ningún endpoint, no muta datos.
 * Todo el output es una simulación orientativa que el investor puede
 * ejercitar libremente.
 *
 * Fórmula (los costes cascada — el management fee se aplica sobre el neto
 * post-costes, no sobre la facturación bruta):
 *
 *   grossAnnual    = 365 * occ * nightlyRate
 *   agencyCost     = grossAnnual * agencyPct
 *   tasaCost       = grossAnnual * 0.10               // tasa local, fija
 *   opAnnualCost   = opMonth * 12
 *   netBeforeMgmt  = gross - agency - tasa - operating
 *   mgmtCost       = max(0, netBeforeMgmt) * mgmtPct
 *   netBeforeTax   = netBeforeMgmt - mgmtCost
 *   taxCost        = taxEnabled ? max(0, netBeforeTax) * taxPct : 0
 *   netAnnual      = netBeforeTax - taxCost
 */
import React, { useMemo, useState } from "react";
import { Section, StatCard } from "@/pages/Shell";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";

const ACCENT = "#2F3E35";
const TASA_PCT = 0.10;

function computeROI({
  price, rate, occPct, agencyPct, opMonth, mgmtPct, taxEnabled, taxPct, years,
}) {
  const purchase    = Math.max(1, Number(price) || 0);
  const nightlyRate = Math.max(0, Number(rate) || 0);
  const occ         = Math.max(0, Math.min(1,  Number(occPct)    / 100));
  const agency      = Math.max(0, Math.min(1,  Number(agencyPct) / 100));
  const mgmt        = Math.max(0, Math.min(1,  Number(mgmtPct)   / 100));
  const tax         = Math.max(0, Math.min(1,  Number(taxPct)    / 100));
  const opM         = Math.max(0, Number(opMonth) || 0);
  const yrs         = Math.max(1, Math.min(30, Math.round(Number(years) || 1)));

  const grossAnnual   = 365 * occ * nightlyRate;
  const agencyCost    = grossAnnual * agency;
  const tasaCost      = grossAnnual * TASA_PCT;
  const opAnnualCost  = opM * 12;

  const netBeforeMgmt = grossAnnual - agencyCost - tasaCost - opAnnualCost;
  const mgmtCost      = Math.max(0, netBeforeMgmt) * mgmt;
  const netBeforeTax  = netBeforeMgmt - mgmtCost;
  const taxCost       = taxEnabled ? Math.max(0, netBeforeTax) * tax : 0;
  const netAnnual     = netBeforeTax - taxCost;

  const grossRoi = (grossAnnual / purchase) * 100;
  const netRoi   = (netAnnual   / purchase) * 100;

  const yearly = [];
  let cumulative = -purchase;
  for (let y = 1; y <= yrs; y++) {
    cumulative += netAnnual;
    yearly.push({
      year: y,
      net_revenue: netAnnual,
      cumulative_cashflow: cumulative,
    });
  }
  const paybackYear = yearly.find(y => y.cumulative_cashflow >= 0)?.year ?? null;

  return {
    purchase,
    gross_annual_revenue: grossAnnual,
    agency_cost: agencyCost,
    tasa_cost: tasaCost,
    operating_cost: opAnnualCost,
    management_cost: mgmtCost,
    tax_cost: taxCost,
    net_annual_revenue: netAnnual,
    gross_roi_pct: Math.round(grossRoi * 100) / 100,
    net_roi_pct:   Math.round(netRoi   * 100) / 100,
    payback_year: paybackYear,
    yearly,
  };
}

export default function ROISimulator() {
  const { t, lang } = useInvestorLanguage();
  const tx = t;
  const locale = lang === "en" ? "en-GB" : "es-ES";
  // Neutral defaults so the calculator never surfaces a pre-populated ROI
  // that could read like a committed promise. Any slider recomputes live.
  const [price, setPrice]           = useState(0);
  const [rate, setRate]             = useState(0);
  const [occ, setOcc]               = useState(0);
  const [agency, setAgency]         = useState(0);
  const [opMonth, setOpMonth]       = useState(0);
  const [mgmt, setMgmt]             = useState(0);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [tax, setTax]               = useState(0);
  const [years, setYears]           = useState(10);

  const result = useMemo(
    () => computeROI({
      price, rate, occPct: occ, agencyPct: agency, opMonth, mgmtPct: mgmt,
      taxEnabled, taxPct: tax, years,
    }),
    [price, rate, occ, agency, opMonth, mgmt, taxEnabled, tax, years],
  );

  const fmtEur0 = (n) => `€${Math.round(n).toLocaleString(locale)}`;
  const fmtEurK = (n) => `€${Math.round(n / 1000).toLocaleString(locale)}k`;

  return (
    <Section title={tx("perf.title")} subtitle={tx("perf.subtitle")}>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="dv-card">
          <Slider label={tx("perf.slider.price")}     value={price}   onChange={setPrice}   min={0}     max={500000} step={1000} unit="€"                            testid="roi-price" />
          <Slider label={tx("perf.slider.rate")}      value={rate}    onChange={setRate}    min={0}     max={500}    step={10}   unit="€"                            testid="roi-rate" />
          <Slider label={tx("perf.slider.occ")}       value={occ}     onChange={setOcc}     min={0}     max={95}     step={1}    unit="%"                            testid="roi-occ" />
          <Slider label={tx("perf.slider.agency")}    value={agency}  onChange={setAgency}  min={0}     max={25}     step={1}    unit="%"                            testid="roi-agency" />
          <FixedRow label={tx("perf.slider.tasa")} value="10%" hint={tx("perf.slider.tasa_locked")} testid="roi-tasa" />
          <Slider label={tx("perf.slider.operating")} value={opMonth} onChange={setOpMonth} min={0}     max={750}    step={25}   unit="€/mes"                        testid="roi-operating" />
          <Slider label={tx("perf.slider.mgmt")}      value={mgmt}    onChange={setMgmt}    min={0}     max={40}     step={1}    unit="%"                            testid="roi-mgmt" />
          <TaxRow
            label={tx("perf.slider.tax")}
            toggleLabel={tx("perf.slider.tax_toggle_on")}
            disabledLabel={tx("perf.slider.tax_disabled")}
            enabled={taxEnabled}
            onToggle={setTaxEnabled}
            value={tax}
            onChange={setTax}
          />
          <Slider label={tx("perf.slider.horizon")}   value={years}   onChange={setYears}   min={1}     max={30}     step={1}    unit={` ${tx("perf.years_unit")}`} testid="roi-years" />
          <div className="text-xs text-stone-500 dark:text-stone-400 mt-2 leading-relaxed">
            {tx("perf.live_recalc")}
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard label={tx("perf.gross_roi")} value={`${result.gross_roi_pct}%`}                                                            accent={ACCENT} testid="roi-gross" />
            <StatCard label={tx("perf.net_roi")}   value={`${result.net_roi_pct}%`}                                                              accent={ACCENT} testid="roi-net" />
            <StatCard label={tx("perf.payback")}   value={result.payback_year ? `${result.payback_year} ${tx("perf.years_unit")}` : "—"}        accent={ACCENT} testid="roi-payback" />
          </div>

          <div className="dv-card">
            <div className="text-xs tracking-widest uppercase text-stone-500 dark:text-stone-400 mb-3">{tx("perf.breakdown.title")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
              <BreakdownCell label={tx("perf.breakdown.gross")}     value={fmtEur0(result.gross_annual_revenue)}      testid="roi-gross-annual" />
              <BreakdownCell label={tx("perf.breakdown.agency")}    value={`- ${fmtEur0(result.agency_cost)}`}        testid="roi-agency-cost" />
              <BreakdownCell label={tx("perf.breakdown.tasa")}      value={`- ${fmtEur0(result.tasa_cost)}`}          testid="roi-tasa-cost" />
              <BreakdownCell label={tx("perf.breakdown.operating")} value={`- ${fmtEur0(result.operating_cost)}`}     testid="roi-operating-cost" />
              <BreakdownCell label={tx("perf.breakdown.mgmt")}      value={`- ${fmtEur0(result.management_cost)}`}    testid="roi-mgmt-cost" />
              {taxEnabled && (
                <BreakdownCell label={tx("perf.breakdown.tax")}     value={`- ${fmtEur0(result.tax_cost)}`}           testid="roi-tax-cost" />
              )}
              <BreakdownCell label={tx("perf.breakdown.net")}       value={fmtEur0(result.net_annual_revenue)} accent testid="roi-net-annual" />
            </div>
          </div>

          <div className="dv-card">
            <div className="text-xs tracking-widest uppercase text-stone-500 dark:text-stone-400 mb-3">{tx("perf.yearly.title")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {result.yearly.map((y) => (
                <div key={y.year} className="bg-stone-50 dark:bg-stone-800 rounded-md p-3 text-center border border-stone-200 dark:border-stone-700" data-testid={`roi-year-${y.year}`} style={{ "--dv-accent": ACCENT }}>
                  <div className="text-xs text-stone-500 dark:text-stone-400">{tx("perf.yearly.year")} {y.year}</div>
                  <div className="font-serif-dv text-xl mt-1 dv-stat-value">
                    {fmtEurK(y.net_revenue)}
                  </div>
                  <div className="text-[10px] text-stone-400 dark:text-stone-500">{tx("perf.yearly.cumulative")} {fmtEurK(y.cumulative_cashflow)}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-stone-400 dark:text-stone-500 mt-4 italic">
              {tx("perf.disclaimer")}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Slider helpers (recovered from the retired Performance page)
// ---------------------------------------------------------------------------

function FixedRow({ label, value, hint, testid }) {
  return (
    <div className="mb-5" data-testid={testid}>
      <div className="flex justify-between mb-2">
        <label className="text-sm text-stone-600 dark:text-stone-300">{label}</label>
        <span className="font-serif-dv text-lg dv-stat-value" style={{ "--dv-accent": ACCENT }}>{value}</span>
      </div>
      <div className="w-full h-[6px] rounded-full bg-stone-200 dark:bg-stone-700 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0" style={{ width: "10%", background: ACCENT }} />
      </div>
      {hint && <div className="text-[11px] text-stone-400 dark:text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

function TaxRow({ label, toggleLabel, disabledLabel, enabled, onToggle, value, onChange }) {
  return (
    <div className="mb-5" data-testid="roi-tax-row">
      <div className="flex justify-between mb-2">
        <label className="text-sm text-stone-600 dark:text-stone-300">{label}</label>
        <span className="font-serif-dv text-lg dv-stat-value" style={{ "--dv-accent": ACCENT }}>
          {enabled ? `${value}%` : disabledLabel}
        </span>
      </div>
      <label className="flex items-center gap-2 text-xs text-stone-600 dark:text-stone-300 mb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="accent-[#2F3E35]"
          data-testid="roi-tax-toggle"
        />
        <span>{toggleLabel}</span>
      </label>
      {enabled && (
        <input
          type="range" min={0} max={35} step={1} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full accent-[#2F3E35]"
          data-testid="roi-tax"
        />
      )}
    </div>
  );
}

function BreakdownCell({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-1">{label}</div>
      <div className={`font-serif-dv text-lg ${accent ? "dv-stat-value" : "text-stone-800 dark:text-stone-100"}`} style={accent ? { "--dv-accent": ACCENT } : undefined}>
        {value}
      </div>
    </div>
  );
}

function Slider({ label, value, onChange, min, max, step, unit, testid }) {
  return (
    <div className="mb-5">
      <div className="flex justify-between mb-2">
        <label className="text-sm text-stone-600 dark:text-stone-300">{label}</label>
        <span className="font-serif-dv text-lg dv-stat-value" style={{ "--dv-accent": ACCENT }}>{Number(value).toLocaleString()}{unit}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#2F3E35]"
        data-testid={testid}
      />
    </div>
  );
}
